<script setup lang="ts">
import type AggregateDialog from '@autobid/ui/components/common/dialog/AggregateRedirectionInfo.vue'
import type { PreparedAuctionItem } from '@autobid/ui/types/components/AuctionsData'
import { useAuctionLink } from '@autobid/ui/composables/useAuctionLink'
import { useOpenLink } from '@autobid/ui/composables/useOpenLink'
import { AGGREGATE_BID_ID } from '@autobid/ui/constants/AGGREGATE_BID_ID'
import { SHOULD_BLOCK_TRANSITION } from '@autobid/ui/constants/AuctionListItem/injectionsKeys'
import { provide } from 'vue'
import AuctionListItemCategory from './AuctionListItemCategory.vue'
import AuctionListImage from './AuctionListItemImage.vue'
import AuctionListItemInfo from './AuctionListItemInfo.vue'
import AuctionListItemLocation from './AuctionListItemLocation.vue'
import AuctionListItemMobile from './AuctionListItemMobile.vue'

const auction = defineProps<PreparedAuctionItem>()
provide('auction', auction)

const { open } = useOpenLink()

const auctionStatusDialog = ref(false)
const link = useAuctionLink({
  appId: auction.appId,
  slug: auction.slug,
  stage: auction.stage,
  startDate: auction.startDate,
  type: auction.type,
})

const isAggregatebidApp = auction.appId === AGGREGATE_BID_ID
const aggregateModalRef = ref<InstanceType<typeof AggregateDialog>>()

const shouldBlockTransition = computed(
  () => isAggregatebidApp && !aggregateModalRef.value?.shouldNotDisplayed,
)

provide('link', link)
provide(SHOULD_BLOCK_TRANSITION, shouldBlockTransition)

function onClick() {
  if (auction.stage === 'IN_PREPARATION' || auction.type === 'LIVE') {
    auctionStatusDialog.value = true
    return
  }

  if (!link.value) {
    return
  }

  if (shouldBlockTransition.value) {
    if (aggregateModalRef.value) {
      aggregateModalRef.value.opened = true
    }

    return
  }

  open(link.value)
}
</script>

<template>
  <div @click="onClick">
    <div
      class="group hidden w-full gap-1 border-b-4 shadow-sm shadow-transparent transition-shadow hover:bg-white hover:shadow-black/10 @lg:grid @lg:grid-cols-[13%,62%,17%,7%] @lg:border-gray-100"
      :class="{
        'cursor-pointer': stage !== 'IN_PREPARATION',
      }"
    >
      <AuctionListItemCategory />
      <AuctionListItemLocation />
      <AuctionListItemInfo />
      <AuctionListImage />
    </div>
    <AuctionListItemMobile />
  </div>

  <LazyDynamicAuctionListItemAuctionStatusDialog
    v-if="auctionStatusDialog"
    :opened="auctionStatusDialog"
    :auction="auction"
    @close="auctionStatusDialog = false"
  />

  <LazyCommonDialogAggregateRedirectionInfo
    v-if="link"
    ref="aggregateModalRef"
    :link="link"
  />
</template>
